/**
 * product-bundle-builder-v2.js
 *
 * <bundle-pdp> custom element — mirrors v1 ProductBundleBuilder UX exactly,
 * but reads from the new $app:bundle_config metaobject shape where selections
 * maps to an array of variant GID strings:
 *
 *   components  — [{ productId, productTitle, variants: [{ id, title, price, image }] }]
 *   combinations — [{ id, label, pricing: { mode, value }, selections: { productGid: variantGid[] }, isDefault }]
 *
 * Currency:
 *   variant.priceCents (sourced from Liquid) is already in the customer's
 *   presentment currency — Shopify Markets converts variant.price at the
 *   Liquid layer automatically. So is anything derived from it (sums,
 *   percentage discounts). The one exception is a combo's 'fixed' pricing
 *   mode, which is a merchant-entered USD dollar amount in the metaobject —
 *   _applyComboDiscount converts that to presentment currency itself, once,
 *   using window.Shopify.currency.rate. _formatMoney only ever formats
 *   (via customFormatMoney(cents, window.moneyFormat), the same formatter
 *   the rest of this theme uses) — it must never apply the rate itself,
 *   since every value reaching it is already presentment-currency by then.
 */

class BundlePdp extends HTMLElement {

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    // Suppress standard PDP price row + ATC form
    const pdpMain = this.closest('.pdp-main');
    if (pdpMain) {
      pdpMain.classList.add('bundle-v2-active');
    }

    this._parseData();
    this._buildSelectableVariants();
    this._initState();
    this._render();
    this._bindEvents();
    this._update();
  }

  // ─── Data ─────────────────────────────────────────────────────────────────

  _parseData() {
    const script = this.querySelector('[data-bundle-v2-data]');
    if (!script) {
      this._data = { components: [], combinations: [], currency: { iso_code: 'USD', symbol: '$' }, ui_strings: {} };
      return;
    }
    try {
      this._data = JSON.parse(script.textContent);
    } catch (e) {
      console.error('[bundle-pdp] Failed to parse bundle data script:', e);
      this._data = { components: [], combinations: [], currency: { iso_code: 'USD', symbol: '$' }, ui_strings: {} };
    }
  }

  /**
   * Build the flat list of selectable variants from ALL components in the
   * catalog — not just those referenced by combinations. This ensures every
   * variant the admin has configured is shown as a tile in the customizer,
   * even if it doesn't appear in any predefined combination.
   *
   * Pricing:
   *   Uses variant.priceCents (market-contextual, in cents) from the Liquid
   *   snippet when available. Shopify Liquid's v.price already reflects any
   *   price list overrides set in Shopify Markets for the current context.
   *   Falls back to Math.round(parseFloat(variant.price) * 100) only when
   *   priceCents is absent or zero (legacy data without the new field).
   */
  _buildSelectableVariants() {
    const { components } = this._data;
    const seen = new Set();
    this._selectableVariants = [];

    (components || []).forEach((product) => {
      (product.variants || []).forEach(variant => {
        const variantGid = variant.id;
        if (seen.has(variantGid)) return; // deduplicate
        seen.add(variantGid);

        // Prefer market-contextual cents from Liquid (v.price, in subunits).
        // Fall back to parsing the legacy dollar-string only if priceCents is absent.
        const priceCents = (typeof variant.priceCents === 'number' && variant.priceCents > 0)
          ? variant.priceCents
          : Math.round(parseFloat(variant.price || '0') * 100);

        // "0" unavailable, "1" low stock (still selectable), "2" available —
        // see snippets/variant-availability.liquid, the theme's single
        // source of truth for market/warehouse-scoped availability.
        const avail = variant.avail || '2';

        const selectable = {
          productGid: product.productId,
          variantGid,
          numericId: parseInt(variantGid.split('/').pop(), 10),
          productTitle: product.productTitle,
          variantTitle: variant.title,
          price: variant.price,
          priceCents,
          image: variant.image || '',
          avail,
          isAvailable: avail !== '0',
          description: product.description || '',
        };

        this._selectableVariants.push(selectable);
      });
    });
  }

  // ─── State ────────────────────────────────────────────────────────────────

  _initState() {
    this._selectedVariants = new Set();
    this._atcInFlight = false;

    // "tiles" (default) = flat grid, each variant independently toggleable.
    // "radio" = one row per product, variants as mutually-exclusive radio inputs.
    // Driven by the "Bundle Components v2" block's layout_style setting so
    // switching modes is a theme-editor dropdown, not a code change.
    this._layoutMode = this._data.layout === 'radio' ? 'radio' : 'tiles';

    // "dim" (default) = show unavailable variants disabled with "Sold out";
    // "hide" = leave them out of the picker entirely. Driven by the "Bundle
    // Components v2" block's unavailable_variant_display setting.
    this._unavailableDisplay = this._data.unavailableDisplay === 'hide' ? 'hide' : 'dim';

    // "bar" (default) = show the isDefault combo as its own "Full Set" bar
    // with its own Add to cart. "preselected" = skip that bar entirely and
    // pre-select the same combo's variants directly in the custom builder
    // instead — straight to preview strip + "Build your set" + grid.
    // Driven by the "Bundle Components v2" block's default_combo_display
    // setting.
    this._defaultComboDisplay = this._data.defaultComboDisplay === 'preselected' ? 'preselected' : 'bar';

    // Find the default combination
    this._defaultCombo = (this._data.combinations || []).find(c => c.isDefault === true);

    // If the default combo includes a now-unavailable variant, treat it the
    // same as "no default combo configured" — skip the Full Set button
    // entirely and go straight to configure mode, reusing that existing
    // path rather than inventing a disabled/partial Full Set state.
    if (this._defaultCombo) {
      const defaultGids = Object.values(this._defaultCombo.selections || {})
        .flatMap(val => Array.isArray(val) ? val : [val]);
      const hasUnavailable = defaultGids.some(gid => {
        const sv = this._selectableVariants.find(v => v.variantGid === gid);
        return sv && !sv.isAvailable;
      });
      if (hasUnavailable) {
        this._defaultCombo = null;
      }
    }

    // Start in full-set mode only when a default combo exists AND it's
    // being shown as its own bar. In "preselected" mode there's no bar to
    // be "in" — the combo's variants are just the builder's starting
    // selection, in ordinary custom mode from the outset (still
    // recognized by _findMatchingCombo, so its discount still applies).
    this._isFullSetMode = !!this._defaultCombo && this._defaultComboDisplay === 'bar';

    // Pre-select all variants from the default combination — in both
    // display modes, this is the starting selection either way.
    if (this._defaultCombo) {
      Object.values(this._defaultCombo.selections || {}).forEach(val => {
        const variantGids = Array.isArray(val) ? val : [val];
        variantGids.forEach(variantGid => {
          this._selectedVariants.add(variantGid);
        });
      });
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  _render() {
    this._renderFullSetButton();
    this._renderCustomizerTitle();
    this._renderComponentButtons();
    this._renderCustomAtcButton();

    // Cache element references
    this._fullSetBar = this.querySelector('.bundle-bar--full-set');
    this._customBar = this.querySelector('.bundle-bar--custom');
    this._componentBtns = this.querySelectorAll('.bundle-button--component');
    this._customAtcBtn = this.querySelector('[data-action="add-custom-to-cart"]');
    this._atcPriceEl = this.querySelector('[data-custom-price]');
  }

  /**
   * Full Set summary bar: title/subtitle on the left, stacked price on the
   * right, and a distinct "Add to cart" button — clicking the button always
   * adds the default combo; clicking elsewhere on the bar just switches
   * INTO full-set mode (e.g. to switch back from custom). That split is
   * deliberate: previously the whole button doubled as both "select" and
   * "add to cart" depending on state, which wasn't obvious from looking at
   * it — a plain price display gave no visual cue it was also live.
   */
  _renderFullSetButton() {
    const container = this.querySelector('[data-default-set-container]');
    if (!container || !this._defaultCombo) return;

    // "preselected" mode skips this bar entirely — the default combo is
    // already the builder's starting selection instead (see _initState()).
    if (this._defaultComboDisplay === 'preselected') return;

    const label = this._defaultCombo.label || 'The full set';
    const flatGids = Object.values(this._defaultCombo.selections || {})
      .flatMap(val => Array.isArray(val) ? val : [val]);
    const baseCents = this._sumVariantPrices(flatGids);
    const finalCents = this._applyComboDiscount(baseCents, this._defaultCombo.pricing);
    const hasDiscount = finalCents < baseCents;
    const itemCount = flatGids.length;

    const subtitle = hasDiscount
      ? `${itemCount} item${itemCount === 1 ? '' : 's'} · save ${this._formatMoney(baseCents - finalCents)}`
      : `${itemCount} item${itemCount === 1 ? '' : 's'}`;

    const priceHtml = hasDiscount
      ? `
          <span class="bundle-bar__price-original"><s>${this._formatMoney(baseCents)}</s></span>
          <span class="bundle-bar__price-final" data-full-set-price>${this._formatMoney(finalCents)}</span>
        `
      : `<span class="bundle-bar__price-final" data-full-set-price>${this._formatMoney(finalCents)}</span>`;

    container.innerHTML = `
      <div class="bundle-bar bundle-bar--full-set" data-action="select-full-set">
        <div class="bundle-bar__info">
          <div class="bundle-bar__title">${label}</div>
          <div class="bundle-bar__subtitle">${subtitle}</div>
        </div>
        <div class="bundle-bar__price">
          ${priceHtml}
        </div>
        <button type="button" class="bundle-bar__cta" data-action="add-full-set-to-cart">Add to cart</button>
      </div>
    `;
  }

  _renderCustomizerTitle() {
    const container = this.querySelector('[data-components-container]');
    if (!container) return;

    let headingHtml;

    if (!this._defaultCombo) {
      // No full-set combo — go straight to the configure heading with no "Build your set" prefix.
      const subheading = this._data.ui_strings?.customizer_subheading || 'Configure your own setup below:';
      headingHtml = `
        <div class="bundle-customizer-title">
          <h3 class="bundle-title">${subheading}</h3>
          <div class="bundle-tier-progress" data-tier-progress></div>
        </div>
      `;
    } else {
      const heading = this._data.ui_strings?.customizer_heading || 'Build your set';
      const subheading = this._data.ui_strings?.customizer_subheading || 'Configure your own setup below:';
      headingHtml = `
        <div class="bundle-customizer-title">
          <h3 class="bundle-title">${heading}</h3>
          <p>${subheading}</p>
          <div class="bundle-tier-progress" data-tier-progress></div>
        </div>
      `;
    }

    container.insertAdjacentHTML('beforebegin', headingHtml);
    this._updateTierProgress();
  }

  /**
   * Render the tier-discount ladder as a progress visualization instead of
   * a chained prose line: one segment per tier, a fill bar showing how
   * close the current selection is to that tier's threshold. Only the
   * tier that's ACTUALLY applying (the highest one the selection qualifies
   * for — see _findQualifyingTier) gets the green "current" treatment;
   * tiers already surpassed but superseded by a better one stay neutral,
   * since only one tier's discount is ever really in effect at a time —
   * coloring all of them green would incorrectly imply they're all active
   * simultaneously. The active tier's label goes bold instead of using a
   * checkmark, so "this is the one that applies" doesn't rely on color
   * alone (colorblind-safe).
   * Hidden entirely when no tiers are configured. Re-run on every _update()
   * (not just the initial render) since the fill/current-tier track the
   * live selection count as items are added or removed.
   */
  _updateTierProgress() {
    const el = this.querySelector('[data-tier-progress]');
    if (!el) return;

    const tiers = [...(this._data.tiers || [])].sort((a, b) => a.minItems - b.minItems);
    if (!tiers.length) {
      el.innerHTML = '';
      return;
    }

    const currentCount = this._selectedVariants.size;
    const qualifyingTier = this._findQualifyingTier(currentCount);

    el.innerHTML = tiers.map(tier => {
      const isCurrent = !!qualifyingTier && qualifyingTier.minItems === tier.minItems;
      const fillPercent = Math.min(currentCount / tier.minItems, 1) * 100;
      return `
        <div class="bundle-tier-progress__step${isCurrent ? ' is-current' : ''}">
          <div class="bundle-tier-progress__bar">
            <div class="bundle-tier-progress__fill" style="width: ${fillPercent}%"></div>
          </div>
          <div class="bundle-tier-progress__label">
            ${tier.minItems} items · ${tier.percent}% off
          </div>
        </div>
      `;
    }).join('');
  }

  _renderComponentButtons() {
    const container = this.querySelector('[data-components-container]');
    if (!container) return;

    container.classList.toggle('bundle-container__components--radio', this._layoutMode === 'radio');
    container.classList.toggle('bundle-container__components--tiles', this._layoutMode !== 'radio');

    if (this._layoutMode === 'radio') {
      this._renderComponentButtonsRadio(container);
    } else {
      this._renderComponentButtonsTiles(container);
    }
  }

  /**
   * "i" info icon opening a mini-PDP modal (image, description, "Learn
   * more" link to the real PDP) for the component product — see
   * _openInfoModal. Rendered as a SIBLING of the tile/radio <button>, not
   * nested inside it: nesting an interactive icon inside an
   * already-interactive <button> is invalid HTML and risks
   * double-activation (Enter/Space on the icon could also fire the
   * button's own click).
   *
   * The description is product-level, not variant-level, so this is meant
   * to be rendered ONCE per product, not once per variant — callers are
   * responsible for that (tiles dedupes by productGid across its flat
   * variant list; radio mode naturally has one call site per product row
   * already). Pass `inline: true` for the radio row-title placement (a
   * plain icon next to text); omit it for the tiles' corner-badge
   * placement (the caller wraps both in a positioned container).
   * Returns '' when there's no description to show.
   */
  _renderInfoIcon(v, { inline = false } = {}) {
    if (!v.description) return '';
    const modifier = inline ? ' bundle-info-icon--inline' : '';
    return `
      <span class="bundle-info-icon${modifier}" tabindex="0" role="button" data-product-gid="${v.productGid}" aria-label="About ${v.productTitle}">i</span>
    `;
  }

  /**
   * Lazily creates the single shared mini-PDP modal (one <dialog>, reused
   * for every "i" icon rather than one dialog per icon) and appends it to
   * <body> — outside this custom element — so it's never affected by any
   * ancestor's overflow/stacking context.
   */
  _getInfoModal() {
    if (this._infoModal) return this._infoModal;

    const modal = document.createElement('dialog');
    modal.className = 'bundle-info-modal';
    modal.innerHTML = `
      <button type="button" class="bundle-info-modal__close" data-info-close aria-label="Close">&times;</button>
      <img class="bundle-info-modal__image" data-info-image alt="" />
      <div class="bundle-info-modal__body">
        <h3 class="bundle-info-modal__title" data-info-title></h3>
        <p class="bundle-info-modal__description" data-info-description></p>
        <a class="bundle-info-modal__link" data-info-link target="_blank" rel="noopener">Learn more →</a>
      </div>
    `;
    document.body.appendChild(modal);

    modal.querySelector('[data-info-close]').addEventListener('click', () => modal.close());
    // Native <dialog> doesn't close on backdrop click by itself — a click
    // that lands on the <dialog> element itself (not its content, which
    // fills only part of it) means the backdrop was clicked.
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.close();
    });

    this._infoModal = modal;
    return modal;
  }

  /** Populate the shared modal with this product's info and open it. */
  _openInfoModal(productGid) {
    const component = (this._data.components || []).find(c => c.productId === productGid);
    if (!component) return;

    const modal = this._getInfoModal();

    modal.querySelector('[data-info-title]').textContent = component.productTitle || '';
    modal.querySelector('[data-info-description]').textContent = component.description || '';

    // Prefer the image of whichever variant of THIS product is currently
    // selected in the bundle (full-set and custom selections both live in
    // _selectedVariants, so this works the same regardless of mode) — that's
    // what the customer will actually receive, not just a generic product
    // shot. Falls back to the first variant with an image, then the
    // product's own generic photo, if nothing's selected yet.
    const productVariants = this._selectableVariants.filter(v => v.productGid === productGid);
    const selectedVariant = productVariants.find(v => this._selectedVariants.has(v.variantGid));
    const fallbackVariant = productVariants.find(v => v.image);
    const modalImage = (selectedVariant && selectedVariant.image)
      || (fallbackVariant && fallbackVariant.image)
      || component.image;

    const imageEl = modal.querySelector('[data-info-image]');
    if (modalImage) {
      imageEl.src = modalImage;
      imageEl.alt = component.productTitle || '';
      imageEl.style.display = '';
    } else {
      imageEl.style.display = 'none';
    }

    // Same variant priority as the image above (selected, then first with
    // an image) so "Learn more" lands the customer on the exact variant
    // they'll actually get, pre-selected on the real PDP via ?variant=.
    const linkEl = modal.querySelector('[data-info-link]');
    if (component.url) {
      const variantForLink = selectedVariant || fallbackVariant;
      if (variantForLink) {
        const separator = component.url.includes('?') ? '&' : '?';
        linkEl.href = `${component.url}${separator}variant=${variantForLink.numericId}`;
      } else {
        linkEl.href = component.url;
      }
      linkEl.style.display = '';
    } else {
      linkEl.style.display = 'none';
    }

    modal.showModal();
  }

  /**
   * Flat grid of independently-toggleable variant tiles (checkbox-style).
   * The info icon is shown once per PRODUCT, not once per variant tile —
   * a product with 3 variants gets 3 tiles but only the first carries the
   * icon, since the description is identical across all of them and
   * repeating it would just be the same tooltip 3 times.
   */
  _renderComponentButtonsTiles(container) {
    const visibleVariants = this._unavailableDisplay === 'hide'
      ? this._selectableVariants.filter(v => v.isAvailable)
      : this._selectableVariants;

    const seenProductsForInfo = new Set();

    container.innerHTML = visibleVariants.map(v => {
      const showVariant = v.variantTitle && v.variantTitle !== 'Default Title';
      const priceOrSoldOut = v.isAvailable
        ? this._formatMoney(v.priceCents)
        : 'Sold out';

      const showInfo = !seenProductsForInfo.has(v.productGid);
      if (showInfo) seenProductsForInfo.add(v.productGid);

      return `
        <div class="bundle-tile-wrap">
          <button
            type="button"
            class="bundle-button bundle-button--component${v.isAvailable ? '' : ' is-unavailable'}"
            role="checkbox"
            aria-checked="false"
            data-variant-gid="${v.variantGid}"
            data-action="toggle-item"
            ${v.isAvailable ? '' : 'disabled'}
          >
            <span class="bundle-btn__title">${v.productTitle}</span>
            ${showVariant ? `<span class="bundle-btn__variant">${v.variantTitle}</span>` : ''}
            <span class="bundle-btn__price">${priceOrSoldOut}</span>
          </button>
          ${showInfo ? this._renderInfoIcon(v) : ''}
        </div>
      `;
    }).join('');
  }

  /**
   * One row per product, its variants listed as role="radio" buttons so only
   * one variant per product can be selected at a time. Grouping preserves
   * the order products first appear in _selectableVariants.
   *
   * These are buttons (not native <input type="radio">), same as the tiles
   * in _renderComponentButtonsTiles — state lives entirely in
   * _selectedVariants, toggled through the single delegated click handler
   * in _bindEvents. Native radios can't be unchecked by clicking them again
   * (the browser only runs "pre-click activation" when going from unchecked
   * to checked), which made a from-scratch deselect-via-preventDefault hack
   * unreliable across repeat clicks. Owning the state as plain buttons
   * avoids that class of bug entirely and matches the tiles' pattern.
   */
  _renderComponentButtonsRadio(container) {
    const groups = [];
    const groupsByProduct = new Map();
    this._selectableVariants.forEach(v => {
      if (!groupsByProduct.has(v.productGid)) {
        const group = { productGid: v.productGid, productTitle: v.productTitle, variants: [] };
        groupsByProduct.set(v.productGid, group);
        groups.push(group);
      }
      groupsByProduct.get(v.productGid).variants.push(v);
    });

    container.innerHTML = groups.map(group => {
      const visibleVariants = this._unavailableDisplay === 'hide'
        ? group.variants.filter(v => v.isAvailable)
        : group.variants;

      // A row with nothing left to show (every variant unavailable, hide
      // mode) contributes nothing rather than an empty row.
      if (visibleVariants.length === 0) return '';

      const options = visibleVariants.map(v => {
        const showVariant = v.variantTitle && v.variantTitle !== 'Default Title';
        const priceOrSoldOut = v.isAvailable
          ? this._formatMoney(v.priceCents)
          : 'Sold out';
        return `
          <button
            type="button"
            class="bundle-radio-option${v.isAvailable ? '' : ' is-unavailable'}"
            role="radio"
            aria-checked="false"
            data-variant-gid="${v.variantGid}"
            data-product-gid="${v.productGid}"
            data-action="toggle-item-radio"
            ${v.isAvailable ? '' : 'disabled'}
          >
            <span class="bundle-radio-option__variant">${showVariant ? v.variantTitle : v.productTitle}</span>
            <span class="bundle-radio-option__price">${priceOrSoldOut}</span>
          </button>
        `;
      }).join('');

      // One icon per product ROW, not per variant pill — every variant here
      // shares the same product (and so the same description), so any one
      // of them (the first) carries the info the icon needs.
      const infoHtml = this._renderInfoIcon(visibleVariants[0], { inline: true });

      return `
        <div class="bundle-radio-row" data-product-gid="${group.productGid}">
          <div class="bundle-radio-row__title-wrap">
            <div class="bundle-radio-row__title">${group.productTitle}</div>
            ${infoHtml}
          </div>
          <div class="bundle-radio-row__options" role="radiogroup">${options}</div>
        </div>
      `;
    }).join('');
  }

  /**
   * Custom bundle summary bar — same layout as the Full Set bar (title/
   * subtitle left, stacked price right, distinct "Add to cart" button).
   * Unlike the Full Set bar, the row itself isn't clickable — selection
   * happens via the tile/radio grid above, not by clicking this summary.
   */
  _renderCustomAtcButton() {
    const container = this.querySelector('[data-custom-atc-container]');
    if (!container) return;

    const label = this._data.ui_strings?.custom_atc_label || 'Custom bundle';
    container.innerHTML = `
      <div class="bundle-bar bundle-bar--custom">
        <div class="bundle-bar__info">
          <div class="bundle-bar__title">${label}</div>
          <div class="bundle-bar__subtitle" data-custom-subtitle>0 items</div>
        </div>
        <div class="bundle-bar__price">
          <span class="bundle-bar__price-original" data-custom-original-price style="display:none;"></span>
          <span class="bundle-bar__price-final" data-custom-price>${this._formatMoney(0)}</span>
        </div>
        <button type="button" class="bundle-bar__cta" data-action="add-custom-to-cart">Add to cart</button>
      </div>
    `;
  }

  /** Switch into full-set mode and pre-select the default combo's variants. */
  _selectFullSet() {
    this._isFullSetMode = true;
    this._selectedVariants.clear();
    if (this._defaultCombo) {
      Object.values(this._defaultCombo.selections || {}).forEach(val => {
        const variantGids = Array.isArray(val) ? val : [val];
        variantGids.forEach(gid => this._selectedVariants.add(gid));
      });
    }
  }

  // ─── Events ───────────────────────────────────────────────────────────────

  _bindEvents() {
    this.addEventListener('click', (e) => {
      // Info icon: open the mini-PDP modal. The icon is a SIBLING of the
      // tile/radio button (not nested inside it — see _renderInfoIcon), so
      // it never reaches the '[data-action]' delegation below on its own.
      const infoIcon = e.target.closest('.bundle-info-icon');
      if (infoIcon) {
        this._openInfoModal(infoIcon.dataset.productGid);
        return;
      }

      // '[data-action]' (not 'button[data-action]') — the Full Set bar's
      // row-level "select" action lives on a wrapping <div>, not a button;
      // only its nested "Add to cart" is an actual <button>. closest()
      // resolves to whichever is nearer the click, so clicking the button
      // always matches the button first.
      const btn = e.target.closest('[data-action]');
      if (!btn) return;

      const action = btn.dataset.action;
      let shouldUpdate = true;

      // Switching from full-set → custom clears selection first
      if (this._isFullSetMode && (action === 'toggle-item' || action === 'toggle-item-radio')) {
        this._selectedVariants.clear();
      }

      switch (action) {
        // Row click (anywhere but the CTA button) — just switch INTO
        // full-set mode, e.g. to switch back after going custom. Does NOT
        // add to cart; only the explicit button below does that, so it's
        // never ambiguous what a given click will do.
        case 'select-full-set':
          this._selectFullSet();
          break;

        // Explicit CTA — always adds the default combo, selecting it first
        // if the customer hadn't already switched into full-set mode.
        case 'add-full-set-to-cart':
          this._selectFullSet();
          this._addToCart('full_set', btn);
          shouldUpdate = false;
          break;

        case 'toggle-item': {
          this._isFullSetMode = false;
          const gid = btn.dataset.variantGid;
          if (this._selectedVariants.has(gid)) {
            this._selectedVariants.delete(gid);
          } else {
            this._selectedVariants.add(gid);
          }
          break;
        }

        case 'toggle-item-radio': {
          this._isFullSetMode = false;
          const gid = btn.dataset.variantGid;
          const productGid = btn.dataset.productGid;

          if (this._selectedVariants.has(gid)) {
            // Click again on the currently-selected option to deselect it —
            // owning this in plain state means there's no native-radio
            // "can't uncheck by clicking" limitation to work around.
            this._selectedVariants.delete(gid);
          } else {
            // Radio semantics: only one variant per product may be
            // selected, so drop any previously-selected variant belonging
            // to this product before adding the newly-chosen one.
            this._selectableVariants
              .filter(v => v.productGid === productGid)
              .forEach(v => this._selectedVariants.delete(v.variantGid));
            this._selectedVariants.add(gid);
          }
          break;
        }

        case 'add-custom-to-cart':
          this._addToCart('custom', btn);
          shouldUpdate = false;
          break;
      }

      if (shouldUpdate) {
        this._update();
      }
    });

    // Keyboard activation for the info icon: it's a <span role="button">,
    // not a real <button> (kept as a sibling of the tile/radio button
    // rather than nested inside it — see _renderInfoIcon), so unlike a
    // real button it doesn't automatically fire 'click' on Enter/Space.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const infoIcon = e.target.closest('.bundle-info-icon');
      if (!infoIcon) return;
      e.preventDefault(); // stop Space from scrolling the page
      this._openInfoModal(infoIcon.dataset.productGid);
    });
  }

  // ─── Reactive UI update ───────────────────────────────────────────────────

  _update() {
    // Bar-level active state — whichever mode is currently selected gets
    // the prominent, filled treatment; the other stays muted. Replaces the
    // old single-button strikethrough with a clearer two-bar contrast.
    this._fullSetBar?.classList.toggle('is-active', this._isFullSetMode);
    this._customBar?.classList.toggle('is-active', !this._isFullSetMode);

    // Component toggles
    if (this._layoutMode === 'radio') {
      this._updateRadioInputs();
    } else {
      this._componentBtns = this.querySelectorAll('.bundle-button--component');
      this._componentBtns.forEach(btn => {
        const gid = btn.dataset.variantGid;
        const isActive = !this._isFullSetMode && this._selectedVariants.has(gid);
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-checked', isActive.toString());
      });
    }

    // Custom bar price/subtitle update — show strikethrough if selection matches a combo with a discount
    this._atcPriceEl = this.querySelector('[data-custom-price]');
    this._atcOriginalPriceEl = this.querySelector('[data-custom-original-price]');
    this._atcSubtitleEl = this.querySelector('[data-custom-subtitle]');
    this._customAtcBtn = this.querySelector('[data-action="add-custom-to-cart"]');

    const selectedGids = [...this._selectedVariants];
    const customBaseCents = this._sumVariantPrices(selectedGids);
    const matchedCombo = this._findMatchingCombo(selectedGids);
    const customFinalCents = this._calcSelectionPrice(selectedGids, customBaseCents, matchedCombo);
    const hasCustomDiscount = customFinalCents < customBaseCents;
    const selectedCount = selectedGids.length;

    if (this._atcSubtitleEl) {
      const base = `${selectedCount} item${selectedCount === 1 ? '' : 's'}`;
      this._atcSubtitleEl.textContent = hasCustomDiscount
        ? `${base} · save ${this._formatMoney(customBaseCents - customFinalCents)}`
        : base;
    }
    if (this._atcPriceEl) {
      this._atcPriceEl.textContent = this._formatMoney(customFinalCents);
    }
    if (this._atcOriginalPriceEl) {
      if (hasCustomDiscount) {
        this._atcOriginalPriceEl.innerHTML = `<s>${this._formatMoney(customBaseCents)}</s>`;
        this._atcOriginalPriceEl.style.display = 'inline';
      } else {
        this._atcOriginalPriceEl.style.display = 'none';
      }
    }
    if (this._customAtcBtn) {
      const isDisabled = this._selectedVariants.size === 0 || this._isFullSetMode;
      this._customAtcBtn.disabled = isDisabled;
    }

    // Tier-discount progress (fill/checkmark track the live selection count)
    this._updateTierProgress();

    // Dynamic bundle preview strip
    this._updatePreviewStrip();

    // Sync combination selection with hidden main variant picker (so availability and pre-order state is synced)
    this._syncMainProductVariant();
  }

  /** Sync active styling + aria-checked on the radio-mode option buttons to current selection. */
  _updateRadioInputs() {
    const options = this.querySelectorAll('.bundle-radio-option');
    options.forEach(option => {
      const gid = option.dataset.variantGid;
      const isActive = !this._isFullSetMode && this._selectedVariants.has(gid);
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-checked', isActive.toString());
    });
  }

  // ─── Bundle Preview Strip ─────────────────────────────────────────────────

  /**
   * Render the dynamic bundle preview strip.
   *
   * Shows a horizontal row of selected component variant thumbnails separated
   * by "+" dividers. In full-set mode the default combo's variants are shown;
   * in custom mode, whatever the customer has toggled on. Thumbnails fade in/
   * out with a CSS transition so the strip feels alive as the selection changes.
   *
   * The strip shell (<div data-bundle-preview>) is rendered in the Liquid
   * snippet so the DOM node always exists — we only set its inner HTML here.
   */
  _updatePreviewStrip() {
    const inner = this.querySelector('[data-bundle-preview-inner]');
    if (!inner) return;

    // Determine which GIDs are currently "active"
    let activeGids = [];
    if (this._isFullSetMode && this._defaultCombo) {
      activeGids = Object.values(this._defaultCombo.selections || {})
        .flatMap(val => Array.isArray(val) ? val : [val]);
    } else {
      activeGids = [...this._selectedVariants];
    }

    // Collect image + meta for each active GID that has an image
    const items = activeGids
      .map(gid => this._selectableVariants.find(sv => sv.variantGid === gid))
      .filter(sv => sv && sv.image);

    if (items.length === 0) {
      inner.innerHTML = '';
      return;
    }

    inner.innerHTML = items.map((sv, idx) => {
      const label = sv.variantTitle && sv.variantTitle !== 'Default Title'
        ? `${sv.productTitle} — ${sv.variantTitle}`
        : sv.productTitle;
      return (
        (idx > 0 ? `<span class="bundle-preview__sep" aria-hidden="true">+</span>` : '') +
        `<div class="bundle-preview__item">
          <img
            class="bundle-preview__img"
            src="${sv.image}"
            alt="${label}"
            width="80" height="80"
            loading="lazy"
          />
          <span class="bundle-preview__label">${label}</span>
        </div>`
      );
    }).join('');
  }

  // ─── Sync Main Variant Selection ─────────────────────────────────────────

  _syncMainProductVariant() {
    const mainSelect = document.querySelector('[data-option-main]');
    if (!mainSelect) return;

    let targetLabel = '';
    if (this._isFullSetMode && this._defaultCombo) {
      targetLabel = this._defaultCombo.label;
    }

    if (!targetLabel) return;

    const clean = str => str.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const cleanTarget = clean(targetLabel);

    const matchedOption = Array.from(mainSelect.options).find(opt => {
      const cleanOptText = clean(opt.text);
      return cleanOptText.includes(cleanTarget) || cleanTarget.includes(cleanOptText);
    });

    if (matchedOption && mainSelect.value !== matchedOption.value) {
      mainSelect.value = matchedOption.value;
      mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ─── Price calculation ────────────────────────────────────────────────────

  /**
   * Apply a combination's pricing rule to a base amount (in cents).
   *   mode: 'percentage' — value is the discount %, e.g. 1 → 1% off
   *   mode: 'fixed'      — value is the final price in USD dollars, e.g. "848.43"
   *
   * We use floating-point arithmetic throughout and only round once at the
   * very end to avoid accumulating rounding errors.
   *
   * Currency: `baseCents` is ALWAYS already in the customer's presentment
   * currency by this point — it's built from variant.priceCents, which
   * Liquid already converts via Shopify Markets (see the "Pricing" note at
   * the top of product-bundle-builder-v2.liquid). 'fixed' mode is the one
   * exception: that value is a merchant-entered USD dollar amount in the
   * metaobject, so it's the one place that genuinely needs the exchange
   * rate applied — and it must happen here, once, so everything leaving
   * this function is consistently presentment-currency cents. _formatMoney
   * must NOT re-apply the rate on top of this (that was the cause of the
   * PDP showing ~98x the correct INR amount — a double conversion).
   */
  _applyComboDiscount(baseCents, pricing) {
    const { mode, value } = pricing || {};
    const numValue = parseFloat(value) || 0;

    if (mode === 'fixed') {
      // value is a dollar amount in the shop's base currency (USD) —
      // convert to presentment currency here, once.
      const rate = this._exchangeRate();
      return Math.round(numValue * 100 * rate);
    }

    if (mode === 'percentage') {
      // value is the discount percentage (e.g. 1 = 1% off)
      // Use float multiply, round once at end
      const multiplier = 1 - numValue / 100;
      const result = Math.round(baseCents * multiplier);
      return Math.max(0, result);
    }

    // no discount
    return baseCents;
  }

  /** Price for the default combo's Full Set button (in USD cents) */
  _calcDefaultComboPrice() {
    if (!this._defaultCombo) return 0;
    const flatGids = Object.values(this._defaultCombo.selections || {})
      .flatMap(val => Array.isArray(val) ? val : [val]);
    const base = this._sumVariantPrices(flatGids);
    return this._applyComboDiscount(base, this._defaultCombo.pricing);
  }

  /**
   * Price for the custom ATC button (in USD cents).
   * If the current selection exactly matches a known combination, apply that
   * combination's discount. Otherwise show the plain sum (no discount).
   */
  _calcCustomPrice() {
    const selectedGids = [...this._selectedVariants];
    const base = this._sumVariantPrices(selectedGids);
    const matchedCombo = this._findMatchingCombo(selectedGids);
    if (matchedCombo) {
      return this._applyComboDiscount(base, matchedCombo.pricing);
    }
    return base;
  }

  /**
   * Return the combination whose selections exactly match the given variant GIDs
   * (order-insensitive), or null if no match.
   */
  _findMatchingCombo(variantGids) {
    const selectedSet = new Set(variantGids);
    return (this._data.combinations || []).find(combo => {
      const comboGids = Object.values(combo.selections || {})
        .flatMap(val => Array.isArray(val) ? val : [val]);
      if (comboGids.length !== selectedSet.size) return false;
      return comboGids.every(gid => selectedSet.has(gid));
    }) || null;
  }

  /**
   * Final price (presentment-currency cents) for a custom selection: exact
   * combo match wins if there is one; otherwise fall back to the tiered
   * quantity discount (e.g. "4+ items -> 4% off", optionally capped by
   * maxAmount) if the selection qualifies for one; plain sum otherwise.
   *
   * This is a live client-side PREVIEW only — the authoritative discount
   * (combo or tier) is computed server-side at cart time.
   */
  _calcSelectionPrice(variantGids, baseCents, matchedCombo) {
    if (matchedCombo) {
      return this._applyComboDiscount(baseCents, matchedCombo.pricing);
    }
    const tier = this._findQualifyingTier(this._countSelectedItems(variantGids));
    if (tier) {
      return this._applyTierDiscount(baseCents, tier);
    }
    return baseCents;
  }

  /**
   * Apply a tiered fallback discount to a presentment-currency base amount,
   * honoring an optional per-tier maxAmount cap:
   *   percentAmount = baseCents * (tier.percent / 100)
   *   discount = min(percentAmount, maxAmount) if maxAmount is set, else percentAmount
   *
   * tier.percent is a plain percentage — no currency involved. tier.maxAmount,
   * when present, is a dollar amount in the shop's BASE currency (USD), same
   * as a combo's 'fixed' price, so — like that path — it needs the exchange
   * rate applied once here to become comparable to percentAmount, which is
   * already presentment-currency (derived from baseCents).
   */
  _applyTierDiscount(baseCents, tier) {
    const percentAmountCents = baseCents * (tier.percent / 100);

    // `tier.maxAmount != null` alone isn't enough: clearing the field in
    // Admin can leave it as 0 or "" rather than truly absent, and both pass
    // `!= null` (0 != null is true) — which would wrongly cap the discount
    // to $0 and make it look like the discount vanished entirely. Treat
    // any non-positive/unparseable value the same as "not set" (uncapped).
    const maxAmount = parseFloat(tier.maxAmount);
    const hasMaxAmount = !isNaN(maxAmount) && maxAmount > 0;

    let discountCents = percentAmountCents;
    if (hasMaxAmount) {
      const maxAmountCents = maxAmount * 100 * this._exchangeRate();
      discountCents = Math.min(percentAmountCents, maxAmountCents);
    }

    const result = Math.round(baseCents - discountCents);
    return Math.max(0, result);
  }

  /**
   * Count used to check tier eligibility. allow_same_variant distinguishes
   * "distinct variants" vs. "total quantity" server-side, but this picker
   * has no per-item quantity stepper — every selection is a Set of variant
   * GIDs (inherently distinct, qty 1 each) — so both counts are identical
   * here today.
   */
  _countSelectedItems(variantGids) {
    return variantGids.length;
  }

  /** Highest-minItems tier the given count qualifies for, or null. */
  _findQualifyingTier(count) {
    const tiers = this._data.tiers || [];
    const qualifying = tiers
      .filter(t => count >= t.minItems)
      .sort((a, b) => b.minItems - a.minItems);
    return qualifying[0] || null;
  }

  /** Sum priceCents for an array of variant GIDs (in USD cents) */
  _sumVariantPrices(variantGids) {
    return variantGids.reduce((total, gid) => {
      const v = this._selectableVariants.find(sv => sv.variantGid === gid);
      return total + (v ? v.priceCents : 0);
    }, 0);
  }

  /**
   * Deterministic id for a set of variant GIDs: same combination (in any
   * selection order) always hashes to the same value; a different
   * combination (even a single variant swapped) hashes to a different one.
   * Sorting before hashing is what makes it order-independent — clicking
   * options in a different sequence must not change the result.
   *
   * Uses djb2 (a small, fast, synchronous string hash) rather than
   * crypto.subtle.digest — we don't need cryptographic properties, just a
   * stable fingerprint, and djb2 avoids making this async.
   */
  _computeBundleGroupId(variantGids) {
    const key = [...variantGids].sort().join('|');
    let hash = 5381;
    for (let i = 0; i < key.length; i++) {
      hash = ((hash << 5) + hash) + key.charCodeAt(i); // hash * 33 + c
      hash = hash >>> 0; // keep it an unsigned 32-bit int
    }
    return `bundle-${hash.toString(36)}`;
  }

  // ─── Add to Cart ──────────────────────────────────────────────────────────

  _addToCart(mode, button) {
    // Guard against a second add-to-cart firing while the first is still
    // in flight — e.g. clicking the custom bundle's "Add to cart" and then,
    // before that request resolves, clicking the Full Set bar's button.
    // Disabling both CTA buttons below closes this for mouse/keyboard
    // clicks (a disabled button never fires 'click'); this flag is the
    // belt-and-suspenders check in case _addToCart is ever invoked another
    // way.
    if (this._atcInFlight) return;
    this._atcInFlight = true;

    let variantGids = [];

    if (mode === 'full_set' && this._defaultCombo) {
      variantGids = Object.values(this._defaultCombo.selections || {})
        .flatMap(val => Array.isArray(val) ? val : [val]);
    } else {
      variantGids = [...this._selectedVariants];
    }

    // Every line item from this ATC action shares this id, so a downstream
    // system (order/checkout processing, or the cart drawer) can regroup
    // the separate line items back into "one bundle" — since we add each
    // chosen variant as its own line item rather than a single merged line.
    // This is deterministic (not random): it's derived from the exact set
    // of variant GIDs being added, sorted so selection order never matters,
    // so the SAME combination (e.g. Unibasket 20g + Moonraker Ultra + RNT
    // Silver) always produces the SAME id, while swapping even one variant
    // (RNT Silver -> Onyx) produces a different one. That lets the app
    // recognize "this is the same bundle combination" across separate ATC
    // actions/customers, not just tie together one cart's line items.
    const bundleGroupId = this._computeBundleGroupId(variantGids);

    // Hidden signal for the Shopify Function + pricing context for cart drawer display.
    // All props starting with _ are hidden from customers in checkout.
    const bundleProps = {
      _bundle: 'true',
      _bundleHostTitle: this._data.hostTitle || '',            // bundle host product title for cart drawer display
      _bundleHostProductId: this._data.hostProductId || '',    // required for server-side tiered-discount lookup
      _bundle_group_id: bundleGroupId,               // ties this ATC action's line items together
    };

    const items = variantGids.map(gid => ({
      id: parseInt(gid.split('/').pop(), 10),
      quantity: 1,
      properties: bundleProps,
    }));

    if (items.length === 0) {
      this._atcInFlight = false;
      return;
    }

    // Lock BOTH bars' CTA buttons, not just the one clicked — a disabled
    // button never fires 'click', which is what actually prevents a second
    // overlapping cart/add.js request.
    this.querySelectorAll('.bundle-bar__cta').forEach(el => {
      el.disabled = true;
    });
    button.classList.add('is-loading');

    const atcUrl = window.Shopify.routes.root + 'cart/add.js';

    fetch(atcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.status && data.status >= 400) {
          throw new Error(data.description || 'Shopify API returned error status.');
        }
        return this._openCartDrawer();
      })
      .catch(err => {
        console.error('[bundle-pdp] ATC API execution failed:', err);
      })
      .finally(() => {
        button.classList.remove('is-loading');
        this._atcInFlight = false;
        // Unconditionally unlock both, then let _update() reapply whichever
        // one should actually stay disabled per normal business logic
        // (e.g. the custom bar's CTA when nothing is selected) — it runs
        // synchronously right after, so there's no visible window where a
        // button is wrongly clickable.
        this.querySelectorAll('.bundle-bar__cta').forEach(el => {
          el.disabled = false;
        });
        this._update();
      });
  }

  _openCartDrawer() {
    const cartUrl = window.Shopify.routes.root + 'cart.js';

    return fetch(cartUrl, {
      headers: { Accept: 'application/json' },
    })
      .then(res => res.json())
      .then(cart => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.open(cart);
          document.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
          );
        }
      });
  }

  // ─── Money formatting ─────────────────────────────────────────────────────

  /** Shopify's own currency.rate (base currency -> customer's presentment currency), or 1.0 if unavailable. */
  _exchangeRate() {
    return (
      window.Shopify &&
      window.Shopify.currency &&
      parseFloat(window.Shopify.currency.rate)
    ) || 1.0;
  }

  /**
   * Format a presentment-currency cents amount using the same
   * customFormatMoney() + window.moneyFormat that the rest of this theme
   * uses (defined in global.js, set in theme.liquid).
   *
   * IMPORTANT: `cents` must already be in the customer's presentment
   * currency by the time it reaches this function — every caller in this
   * file sources its amounts from variant.priceCents (already
   * Markets-converted by Liquid) or from _applyComboDiscount's 'fixed'
   * branch (which converts its raw USD value itself). This function must
   * NOT apply Shopify.currency.rate on top of that — doing so previously
   * caused a double conversion (PDP showing ~98x the correct INR amount).
   *
   * Fallback chain (most to least reliable):
   *   1. customFormatMoney(cents, moneyFormat)  — theme's own formatter
   *   2. Intl.NumberFormat with active currency code
   *   3. Plain dollar sign + number
   */
  _formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
    const convertedCents = Math.round(cents);

    // Use the theme's existing customFormatMoney if available
    if (typeof customFormatMoney === 'function' && window.moneyFormat) {
      return customFormatMoney(convertedCents, window.moneyFormat);
    }

    // Fallback: Intl.NumberFormat with the active display currency
    const currencyCode = (
      window.Shopify && window.Shopify.currency && window.Shopify.currency.active
    ) || this._data.currency?.iso_code || 'USD';

    try {
      const hasCents = convertedCents % 100 !== 0;
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
      }).format(convertedCents / 100);
    } catch (e) {
      return (this._data.currency?.symbol || '$') + (convertedCents / 100).toFixed(0);
    }
  }
}

customElements.define('bundle-pdp', BundlePdp);
